import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock3, ShieldAlert, Trash2 } from 'lucide-react';
import { idemApi } from '../lib/api';
import { clearIdemDeviceToken } from '../lib/idemSession';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function StatusBadge({ status }) {
  const tone = {
    active: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    revoked: 'border-red-300 bg-red-50 text-red-700',
    pending: 'border-amber-300 bg-amber-50 text-amber-800',
  }[status] || 'border-zinc-200 bg-zinc-50 text-zinc-600';
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${tone}`}>{String(status || 'active').replaceAll('_', ' ')}</span>;
}

function formatSessionError(value) {
  if (!value) return '';
  if (value === 'digital_key_authenticator_session_not_found') {
    return 'Stored device token could not be verified. Use Forget this device if needed and pair again.';
  }
  return value;
}

export default function IdemAccount() {
  const [session, setSession] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [sessionError, setSessionError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const { data } = await idemApi.get('/idem/security');
      setSession(data.session || null);
      setRequests((data.recent_requests || []).filter((request) => request.status === 'pending'));
      setSessionError(data.session_error || '');
    } catch (requestError) {
      setSession(null);
      setRequests([]);
      setSessionError('');
      setError(requestError?.response?.data?.error || 'The paired lab account could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pendingCount = useMemo(() => requests.filter((request) => request.status === 'pending').length, [requests]);

  const forget = () => {
    clearIdemDeviceToken();
    setSession(null);
    setRequests([]);
    setSessionError('');
    setError('');
    setNotice('Local device token cleared from this browser.');
  };

  return (
    <main className="min-h-screen bg-zinc-100 px-4 py-6 text-zinc-900 sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-4xl">
        <header className="flex items-center gap-2.5 border-b border-zinc-200 pb-5">
          <div className="grid h-9 w-9 place-items-center bg-black text-sm font-semibold text-white">i</div>
          <div>
            <p className="font-display text-lg font-bold tracking-tight">idem</p>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Lab account</span>
          </div>
        </header>

        <section className="mt-5 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
            <div>
              <p className="font-medium">Lab authenticator device</p>
              <p className="mt-1 text-xs leading-5">This account view is for the internal idem lab pairing flow only. It is not a production CSAM/FAS authenticator, public login surface, or official LoA flow.</p>
            </div>
          </div>
        </section>

        {notice && <div className="mt-5 border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{notice}</div>}
        {sessionError && <div className="mt-5 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{formatSessionError(sessionError)}</div>}
        {error && <div className="mt-5 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        {loading ? (
          <div className="mt-6 border border-zinc-200 bg-white p-5 text-sm text-zinc-500">Loading paired account…</div>
        ) : session ? (
          <>
            <section className="mt-6 border border-zinc-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Paired session</p>
                  <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Lab authenticator paired</h1>
                  <p className="mt-2 max-w-2xl text-sm text-zinc-600">This browser now carries a lab device session for the linked digital key account.</p>
                </div>
                <StatusBadge status={session.status} />
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <article className="border border-zinc-100 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Digital key account</p>
                  <p className="mt-1 break-all font-mono text-xs text-zinc-700">{session.digital_key_account_id}</p>
                  <p className="mt-1 text-xs text-zinc-500">{session.account_status || '—'}</p>
                </article>
                <article className="border border-zinc-100 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Device</p>
                  <p className="mt-1 text-sm text-zinc-700">{session.device_label}</p>
                  <p className="mt-1 font-mono text-xs text-zinc-500">{session.device_public_id}</p>
                </article>
                <article className="border border-zinc-100 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Identity reference</p>
                  <p className="mt-1 text-sm text-zinc-700">{session.identity_display_name || session.identity_public_id || '—'}</p>
                  <p className="mt-1 font-mono text-xs text-zinc-500">{session.identity_public_id || session.identity_id || '—'}</p>
                </article>
                <article className="border border-zinc-100 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Last seen</p>
                  <p className="mt-1 text-sm text-zinc-700">{formatDate(session.last_seen_at)}</p>
                  <p className="mt-1 text-xs text-zinc-500">Created {formatDate(session.created_at || session.device_token_issued_at)}</p>
                </article>
              </div>

              {session.revoked_at && (
                <div className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  This paired device was revoked at {formatDate(session.revoked_at)}.
                </div>
              )}

              {session.status === 'revoked' && (
                <div className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  This paired device is revoked. Pair a new device or use Forget this device to clear the local token.
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Link to="/idem/requests" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open requests</Link>
                <Link to="/idem/security" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open security</Link>
                <button type="button" onClick={forget} className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Forget this device
                </button>
              </div>
            </section>

            <section className="mt-6 border border-zinc-200 bg-white p-5">
              <div className="flex items-center gap-2">
                <Clock3 className="h-4 w-4 text-zinc-500" strokeWidth={1.5} />
                <h2 className="font-display text-lg font-semibold">Pending requests</h2>
              </div>
              <p className="mt-1 text-sm text-zinc-600">{pendingCount ? `${pendingCount} pending request(s) for this paired account.` : 'No pending requests are waiting for this paired account.'}</p>
              {requests.length ? (
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      <tr>
                        <th className="py-2 pr-3 font-medium">Request</th>
                        <th className="py-2 pr-3 font-medium">Identity</th>
                        <th className="py-2 pr-3 font-medium">Status</th>
                        <th className="py-2 pr-3 font-medium">Assurance</th>
                        <th className="py-2 font-medium">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((request) => (
                        <tr key={request.id} className="border-b border-zinc-100 last:border-b-0">
                          <td className="py-3 pr-3 font-mono text-xs text-zinc-700">{request.id}</td>
                          <td className="py-3 pr-3 text-xs text-zinc-600">{request.identity_display_name || request.identity_public_id || request.identity_id || '—'}</td>
                          <td className="py-3 pr-3 text-xs text-zinc-600">{String(request.status || 'pending').replaceAll('_', ' ')}</td>
                          <td className="py-3 pr-3 text-xs text-zinc-600">{String(request.requested_assurance || 'internal_foundation').replaceAll('_', ' ')}</td>
                          <td className="whitespace-nowrap py-3 text-xs text-zinc-500">{formatDate(request.updated_at || request.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </section>
          </>
        ) : (
          <section className="mt-6 border border-zinc-200 bg-white p-5">
            <p className="font-display text-xl font-semibold tracking-tight text-zinc-900">No paired device yet</p>
            <p className="mt-2 max-w-2xl text-sm text-zinc-600">Open an activation link to pair this authenticator with a digital key account. If you already activated a device, the browser token may have been cleared.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/idem/requests" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open requests</Link>
              <Link to="/idem/security" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open security</Link>
              <button type="button" onClick={forget} className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                Forget this device
              </button>
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
